import { useEffect, useState } from 'react';
import Wordmark from '../components/Wordmark.jsx';
import FilterBar from '../components/proofing/FilterBar.jsx';
import FinalDownloadsPanel from '../components/proofing/FinalDownloadsPanel.jsx';
import ProofImageGrid from '../components/proofing/ProofImageGrid.jsx';
import ProofLightbox from '../components/proofing/ProofLightbox.jsx';
import ProofUploadDropzone from '../components/proofing/ProofUploadDropzone.jsx';
import { loginAdmin } from '../utils/api.js';
import {
  createProofingGallery,
  deleteFinalAsset,
  deleteProofImage,
  deleteProofingGallery,
  fetchProofingGalleries,
  fetchProofingGallery,
  updateProofImage,
  updateProofingGallery
} from '../utils/proofingApi.js';

const adminPasswordKey = 'maldet-admin-password';

function countImages(images) {
  return images.reduce(
    (counts, image) => {
      counts.all += 1;
      counts[image.rating || 'unrated'] += 1;
      return counts;
    },
    { all: 0, unrated: 0, red: 0, yellow: 0, green: 0, final: 0 }
  );
}

function NewGalleryForm({ onCreated }) {
  const [status, setStatus] = useState('');

  async function handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    try {
      const result = await createProofingGallery(payload);
      form.reset();
      setStatus('Galerie angelegt.');
      onCreated(result.gallery);
    } catch (error) {
      setStatus(error.message);
    }
  }

  return (
    <form className="proof-create" onSubmit={handleSubmit}>
      <p className="mono-label">New private room</p>
      <h2>Galerie anlegen</h2>
      <label>
        Titel
        <input name="title" placeholder="Leni / Editorial Night" required />
      </label>
      <label>
        Model / Kunde
        <input name="modelName" placeholder="Modelname" />
      </label>
      <label>
        Slug optional
        <input name="slug" placeholder="leni-editorial-night" />
      </label>
      <label>
        Passwort
        <input name="password" type="password" minLength="4" required />
      </label>
      <label className="proof-create__wide">
        Beschreibung
        <textarea name="description" rows="3" placeholder="Kurze Notiz zum Shooting" />
      </label>
      <button type="submit">Galerie erstellen</button>
      {status && <p className="admin-status">{status}</p>}
    </form>
  );
}

function GallerySettings({ detail, onSaved, onDeleted }) {
  const [status, setStatus] = useState('');

  async function handleSubmit(event) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const payload = Object.fromEntries(formData.entries());
    payload.isActive = formData.get('isActive') === 'on';
    payload.isArchived = formData.get('isArchived') === 'on';

    if (!payload.password) delete payload.password;

    try {
      const result = await updateProofingGallery(detail.gallery.id, payload);
      setStatus('Gespeichert.');
      onSaved(result.gallery);
    } catch (error) {
      setStatus(error.message);
    }
  }

  async function handleDelete() {
    const ok = window.confirm(`Galerie "${detail.gallery.title}" mit allen Dateien löschen?`);
    if (!ok) return;

    try {
      await deleteProofingGallery(detail.gallery.id);
      onDeleted(detail.gallery.id);
    } catch (error) {
      setStatus(error.message);
    }
  }

  return (
    <form className="proof-settings" onSubmit={handleSubmit}>
      <label>
        Titel
        <input name="title" defaultValue={detail.gallery.title} />
      </label>
      <label>
        Model / Kunde
        <input name="modelName" defaultValue={detail.gallery.modelName} />
      </label>
      <label>
        Slug
        <input name="slug" defaultValue={detail.gallery.slug} />
      </label>
      <label>
        Neues Passwort
        <input name="password" type="password" placeholder="Leer lassen, um es zu behalten" />
      </label>
      <label className="proof-settings__wide">
        Beschreibung
        <textarea name="description" rows="4" defaultValue={detail.gallery.description} />
      </label>
      <label className="proof-check">
        <input name="isActive" type="checkbox" defaultChecked={detail.gallery.isActive} />
        Aktiv
      </label>
      <label className="proof-check">
        <input name="isArchived" type="checkbox" defaultChecked={detail.gallery.isArchived} />
        Archiviert
      </label>
      <button type="submit">Speichern</button>
      <button type="button" className="danger" onClick={handleDelete}>
        Galerie löschen
      </button>
      {status && <p className="admin-status">{status}</p>}
    </form>
  );
}

export default function ProofingAdmin() {
  const [password, setPassword] = useState(() => localStorage.getItem(adminPasswordKey) || '');
  const [authenticated, setAuthenticated] = useState(() => Boolean(localStorage.getItem(adminPasswordKey)));
  const [galleries, setGalleries] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('proofs');
  const [filter, setFilter] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [status, setStatus] = useState('');

  async function loadGalleries(selectId = selectedId) {
    const nextGalleries = await fetchProofingGalleries();
    setGalleries(nextGalleries);
    const nextId = selectId || nextGalleries[0]?.id || '';
    setSelectedId(nextId);
    if (nextId) await loadDetail(nextId);
  }

  async function loadDetail(id = selectedId) {
    if (!id) {
      setDetail(null);
      return;
    }
    const payload = await fetchProofingGallery(id);
    setDetail(payload);
  }

  useEffect(() => {
    if (!authenticated) return;
    loadGalleries().catch((error) => {
      setStatus(error.message);
      setAuthenticated(false);
      localStorage.removeItem(adminPasswordKey);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  async function handleLogin(event) {
    event.preventDefault();
    try {
      await loginAdmin(password);
      localStorage.setItem(adminPasswordKey, password);
      setAuthenticated(true);
      setStatus('');
    } catch (error) {
      setStatus(error.message);
    }
  }

  async function handleRate(image, rating) {
    if (!detail) return;
    const previous = detail.images;
    const nextImages = detail.images.map((item) => (item.id === image.id ? { ...item, rating } : item));
    setDetail({ ...detail, images: nextImages });
    setLightboxImage((current) => (current?.id === image.id ? { ...current, rating } : current));
    try {
      const result = await updateProofImage(image.id, { rating });
      setDetail((current) => ({
        ...current,
        images: current.images.map((item) => (item.id === image.id ? result.image : item))
      }));
    } catch (error) {
      setStatus(error.message);
      setDetail({ ...detail, images: previous });
    }
  }

  async function handleDeleteImage(image) {
    const ok = window.confirm(`Proof "${image.filename}" löschen?`);
    if (!ok) return;
    await deleteProofImage(image.id);
    await loadDetail();
  }

  async function handleDeleteFinal(asset) {
    const ok = window.confirm(`Finaldatei "${asset.filename}" löschen?`);
    if (!ok) return;
    await deleteFinalAsset(asset.id);
    await loadDetail();
  }

  if (!authenticated) {
    return (
      <main className="admin-page admin-page--login">
        <div className="admin-login">
          <Wordmark />
          <p className="mono-label">Proofing command room</p>
          <h1>Client Galleries</h1>
          <form onSubmit={handleLogin}>
            <label>
              Admin Passwort
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            </label>
            <button type="submit">Enter proofing</button>
          </form>
          {status && <p className="admin-status">{status}</p>}
        </div>
      </main>
    );
  }

  const images = detail?.images || [];
  const counts = countImages(images);
  const filteredImages = images.filter((image) => {
    if (filter === 'all') return true;
    if (filter === 'unrated') return !image.rating;
    return image.rating === filter;
  });
  const finals = detail
    ? {
        uploadedFinals: detail.finalAssets,
        proofSelections: detail.images.filter((image) => image.rating === 'final')
      }
    : { uploadedFinals: [], proofSelections: [] };

  return (
    <main className="proof-admin">
      <header className="proof-admin__header">
        <div>
          <Wordmark compact />
          <p className="mono-label">Proofing / delivery platform</p>
          <h1>Kundengalerien</h1>
        </div>
        <nav>
          <a href="/">Experience</a>
          <a href="/admin">Website Admin</a>
        </nav>
      </header>

      <section className="proof-admin__shell">
        <aside className="proof-sidebar">
          <NewGalleryForm
            onCreated={async (gallery) => {
              await loadGalleries(gallery.id);
              setActiveTab('proofs');
            }}
          />
          <div className="proof-gallery-list">
            {galleries.map((gallery) => (
              <button
                type="button"
                className={gallery.id === selectedId ? 'is-active' : ''}
                key={gallery.id}
                onClick={async () => {
                  setSelectedId(gallery.id);
                  setFilter('all');
                  await loadDetail(gallery.id);
                }}
              >
                <span>{gallery.title}</span>
                <small>
                  {gallery.modelName || 'No model'} / {gallery.stats.images} Proofs / {gallery.stats.ratings.final} Final
                </small>
              </button>
            ))}
          </div>
        </aside>

        <section className="proof-workspace">
          {!detail ? (
            <div className="proof-empty">
              <p className="mono-label">No gallery selected</p>
              <h3>Lege links eine Kundengalerie an.</h3>
            </div>
          ) : (
            <>
              <div className="proof-workspace__hero">
                <div>
                  <p className="mono-label">{detail.gallery.modelName || 'Private Gallery'}</p>
                  <h2>{detail.gallery.title}</h2>
                  <p>{detail.gallery.description || 'Geschützter Raum für Auswahl, Freigabe und Bildauslieferung.'}</p>
                </div>
                <div className="proof-link-box">
                  <span>{window.location.origin}/client/{detail.gallery.slug}</span>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/client/${detail.gallery.slug}`)}
                  >
                    Link kopieren
                  </button>
                </div>
              </div>

              <div className="proof-tabs">
                {['proofs', 'finals', 'settings'].map((tab) => (
                  <button
                    type="button"
                    className={activeTab === tab ? 'is-active' : ''}
                    onClick={() => setActiveTab(tab)}
                    key={tab}
                  >
                    {tab === 'proofs' ? 'Proofs' : tab === 'finals' ? 'Finale Bilder' : 'Settings'}
                  </button>
                ))}
              </div>

              {activeTab === 'proofs' && (
                <>
                  <ProofUploadDropzone
                    galleryId={detail.gallery.id}
                    kind="proofs"
                    label="Drag / drop proofs"
                    hint="Mehrere JPG, PNG oder WebP Dateien hochladen. Originale bleiben erhalten, Grid nutzt optimierte Previews."
                    onUploaded={() => {
                      loadDetail();
                      loadGalleries(selectedId);
                    }}
                  />
                  <FilterBar value={filter} onChange={setFilter} counts={counts} />
                  <ProofImageGrid
                    images={filteredImages}
                    admin
                    onOpen={setLightboxImage}
                    onRate={handleRate}
                    onDelete={handleDeleteImage}
                  />
                </>
              )}

              {activeTab === 'finals' && (
                <>
                  <ProofUploadDropzone
                    galleryId={detail.gallery.id}
                    kind="finals"
                    label="Upload finals"
                    hint="Separate finale High-Res-Dateien für die Auslieferung. Wenn leer, nutzt der ZIP-Download final markierte Proofs."
                    onUploaded={() => loadDetail()}
                  />
                  <FinalDownloadsPanel slug={detail.gallery.slug} finals={finals} adminGalleryId={detail.gallery.id} />
                  <div className="final-admin-list">
                    {detail.finalAssets.map((asset) => (
                      <article key={asset.id}>
                        <span>{asset.filename}</span>
                        <button type="button" onClick={() => handleDeleteFinal(asset)}>
                          Löschen
                        </button>
                      </article>
                    ))}
                  </div>
                </>
              )}

              {activeTab === 'settings' && (
                <GallerySettings
                  detail={detail}
                  onSaved={(gallery) => {
                    setDetail({ ...detail, gallery });
                    loadGalleries(gallery.id);
                  }}
                  onDeleted={() => {
                    setDetail(null);
                    setSelectedId('');
                    loadGalleries('');
                  }}
                />
              )}
            </>
          )}
        </section>
      </section>

      {status && <p className="floating-status">{status}</p>}
      <ProofLightbox
        image={lightboxImage}
        images={filteredImages}
        onClose={() => setLightboxImage(null)}
        onSelect={setLightboxImage}
        onRate={handleRate}
        admin
      />
    </main>
  );
}
